import { toRef, MaybeRefOrGetter } from "@vueuse/core";
import { watch } from "vue";

import { getClient } from "./client";
import { useAsyncState } from "@vueuse/core";
import { ResponseStatus } from "@/lib/useFormResponse";

export type MilestoneType =
  | "engineering_drawings_30_completed"
  | "engineering_drawings_100_completed"
  | "major_equipment_ordered"
  | "permit_approved_ntp_issued"
  | "site_prep_started"
  | "site_prep_completed"
  | "electrical_mechanical_installation_started"
  | "electrical_mechanical_installation_completed"
  | "interconnection_approved"
  | "testing_comissioning_completed"
  | "commercial_operation_date";

export const MilestoneTypeDict: Record<MilestoneType, string> = {
  engineering_drawings_30_completed: "Engineering Drawings 30% completed",
  engineering_drawings_100_completed: "Engineering Drawings 100% completed",
  major_equipment_ordered: "Major Equipment Ordered",
  permit_approved_ntp_issued: "Permit Approved and NTP Issued",
  site_prep_started: "Site Prep Started",
  site_prep_completed: "Site Prep Completed",
  electrical_mechanical_installation_started:
    "Electrical Mechanical Installation Started",
  electrical_mechanical_installation_completed:
    "Electrical Mechanical Installation Completed",
  interconnection_approved: "Interconnection Approved",
  testing_comissioning_completed: "Testing Commissioning Completed",
  commercial_operation_date: "Commercial Operation Date",
};

export type Milestone = {
  formResponseId: string;
  milestoneType: MilestoneType;
  milestoneDate: string;
  created: Date;
  updated: Date;
  status: ResponseStatus;
};

type MilestoneAPIResponse = {
  form_response_id: string;
  milestone_type: MilestoneType;
  milestone_date: string;
  created: string;
  updated: string;
  status: ResponseStatus;
};

type MilestonesAPIResponse = Record<MilestoneType, MilestoneAPIResponse>;

export type Milestones = Milestone[];

export type Timeline = {
  id: string;
  proposalId: string;
  formId: string;
  created: Date;
  updated: Date;
  milestones: Milestones;
};

type TimelineAPIResponse = {
  id: string;
  proposal_id: string;
  form_id: string;
  created: string;
  updated: string;
  milestones: MilestonesAPIResponse;
};

export function useTimeline(
  proposalId: MaybeRefOrGetter<string | undefined>,
  formResponseId: MaybeRefOrGetter<string | undefined>,
) {
  const reactiveProposalId = toRef<string | undefined>(proposalId);
  const reactiveFormResponseId = toRef<string | undefined>(formResponseId);
  const client = getClient();

  const {
    state: timelineState,
    isReady: timelineReady,
    isLoading: timelineLoading,
    error: timelineError,
    execute: reloadTimeline,
  } = useAsyncState<Timeline | null>(
    async () => {
      if (reactiveProposalId.value && reactiveFormResponseId.value) {
        const res = await client.request<TimelineAPIResponse>({
          url: `/proposals/${encodeURIComponent(
            reactiveProposalId.value,
          )}/form_responses/${encodeURIComponent(
            reactiveFormResponseId.value,
          )}/timeline`,
        });

        // TODO: Check for non 200 statuses and do something
        // TODO: Bubble up timeline error state correctly so that a not-found is shown by the parent.

        const milestones: Milestones = [];
        for (const milestone of Object.values(res.data.milestones)) {
          milestones.push({
            formResponseId: milestone.form_response_id,
            milestoneType: milestone.milestone_type,
            milestoneDate: milestone.milestone_date,
            created: new Date(milestone.created),
            updated: new Date(milestone.updated),
            status: milestone.status,
          });
        }
        return {
          id: res.data.id,
          proposalId: res.data.proposal_id,
          formId: res.data.form_id,
          created: new Date(res.data.created),
          updated: new Date(res.data.updated),
          milestones,
        };
      } else {
        return null;
      }
    },
    null,
    { resetOnExecute: false },
  );

  async function updateMilestone(
    milestoneType: MilestoneType,
    milestoneDate: string,
    shouldGenerateComment: boolean,
  ) {
    if (reactiveProposalId.value && reactiveFormResponseId.value) {
      const res = await client.post<TimelineAPIResponse>(
        `/proposals/${encodeURIComponent(
          reactiveProposalId.value,
        )}/form_responses/${encodeURIComponent(
          reactiveFormResponseId.value,
        )}/timeline/${encodeURIComponent(milestoneType)}`,
        undefined,
        {
          params: {
            milestone_date: milestoneDate,
            generate_comment: shouldGenerateComment,
          },
        },
      );
      if (res.status !== 200) {
        throw new Error(
          `Update Milestone failed for milestone type: ${milestoneType} and milestone date: ${milestoneDate}`,
        );
      }
    }
  }

  async function deleteMilestone(
    milestoneType: MilestoneType,
    shouldGenerateComment: boolean,
  ) {
    if (reactiveProposalId.value && reactiveFormResponseId.value) {
      const res = await client.delete<TimelineAPIResponse>(
        `/proposals/${encodeURIComponent(
          reactiveProposalId.value,
        )}/form_responses/${encodeURIComponent(
          reactiveFormResponseId.value,
        )}/timeline/${encodeURIComponent(milestoneType)}`,
        {
          params: {
            generate_comment: shouldGenerateComment,
          },
        },
      );
      if (res.status !== 200) {
        throw new Error(
          `Delete Milestone failed for milestone type: ${milestoneType}`,
        );
      }
    }
  }

  watch([reactiveProposalId, reactiveFormResponseId], () => reloadTimeline());

  return {
    timelineState,
    timelineReady,
    timelineLoading,
    timelineError,
    reloadTimeline,
    updateMilestone,
    deleteMilestone,
  };
}
